import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Grid,
  Chip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { CheckCircle, Cancel, Sync } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: -8,
    marginBottom: 8
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

const ConnectionSchema = Yup.object().shape({
  name: Yup.string().required("Nome é obrigatório"),
  wabaId: Yup.string().required("WABA ID é obrigatório"),
  phoneNumberId: Yup.string().required("Phone Number ID é obrigatório"),
  metaAccessToken: Yup.string()
});

const initialState = {
  name: "",
  wabaId: "",
  phoneNumberId: "",
  number: "",
  metaAccessToken: "",
  metaVerifyToken: ""
};

const WhatsAppModalMetaCloud = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [connection, setConnection] = useState(initialState);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!whatsAppId) {
      setConnection(initialState);
      setTestResult(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/meta-cloud");
        const found = data.find(c => c.id === whatsAppId);
        if (found) {
          setConnection({
            ...initialState,
            ...found,
            metaAccessToken: "" // nunca vem do backend; deixa vazio (mantém o já salvo se não digitar nada)
          });
        }
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId, open]);

  const handleClose = () => {
    setConnection(initialState);
    setTestResult(null);
    onClose();
  };

  const handleSaveConnection = async values => {
    try {
      if (whatsAppId) {
        await api.put(`/meta-cloud/${whatsAppId}`, values);
      } else {
        await api.post("/meta-cloud", values);
      }
      toast.success("Conexão Meta salva com sucesso!");
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleTestConnection = async () => {
    if (!whatsAppId) {
      toast.warn("Salve a conexão antes de testar.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post(`/meta-cloud/${whatsAppId}/test-connection`);
      setTestResult(data);
      if (data.ok) {
        toast.success("Conexão com a Meta funcionando!");
      } else {
        toast.error(data.error || "Falha ao testar conexão.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {whatsAppId ? "Editar Conexão WhatsApp Oficial (Meta)" : "Nova Conexão WhatsApp Oficial (Meta)"}
      </DialogTitle>
      <Formik
        initialValues={connection}
        enableReinitialize
        validationSchema={ConnectionSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveConnection(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ values, touched, errors, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2} className={classes.root}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Nome da conexão"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label="WABA ID"
                    name="wabaId"
                    error={touched.wabaId && Boolean(errors.wabaId)}
                    helperText={touched.wabaId && errors.wabaId}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label="Phone Number ID"
                    name="phoneNumberId"
                    error={touched.phoneNumberId && Boolean(errors.phoneNumberId)}
                    helperText={touched.phoneNumberId && errors.phoneNumberId}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Número do WhatsApp"
                    name="number"
                    placeholder="5511999999999"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Access Token"
                    name="metaAccessToken"
                    type="password"
                    placeholder={whatsAppId ? "Deixe em branco para manter o atual" : ""}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Verify Token (Webhook)"
                    name="metaVerifyToken"
                    variant="outlined"
                    fullWidth
                  />
                  <Typography className={classes.helperText}>
                    Configure na Meta o Webhook apontando para{" "}
                    <code>{`${process.env.REACT_APP_BACKEND_URL}/webhooks/meta`}</code>{" "}
                    usando este mesmo Verify Token.
                  </Typography>
                </Grid>

                {whatsAppId && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Sync />}
                      disabled={testing}
                      onClick={handleTestConnection}
                    >
                      {testing ? "Testando..." : "Testar conexão"}
                    </Button>

                    {testResult && (
                      <div className={classes.statusRow}>
                        {testResult.ok ? (
                          <Chip
                            icon={<CheckCircle />}
                            label={`Conectado: ${testResult.phoneNumber || ""} (${
                              testResult.verifiedName || ""
                            })`}
                            color="primary"
                          />
                        ) : (
                          <Chip
                            icon={<Cancel />}
                            label={testResult.error || "Falha na conexão"}
                            color="secondary"
                          />
                        )}
                      </div>
                    )}
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={20} /> : "Salvar"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default WhatsAppModalMetaCloud;
